import { rememberApiError } from '@bayesprep/shared-ui/errors';
import { confirmSessionFailure } from './sessionFailure';
import axios, { AxiosInstance } from 'axios';

// Extend window interface for logging state
declare global {
  interface Window {
    lastLoggedTenantId?: string;
    lastLoggedUserEmail?: string;
  }
}

// Create axios instance with base configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and tenant headers
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    (config as any).__sessionToken = token;
    if (token) {
      // Ensure token is properly formatted
      let formattedToken = token;
      if (!token.startsWith('Bearer ')) {
        formattedToken = `Bearer ${token}`;
      }
      config.headers.Authorization = formattedToken;
    }

    // Add tenant header - extract from JWT token or use stored tenant
    const tenantCode = localStorage.getItem('tenantCode');
    if (tenantCode) {
      config.headers['X-Tenant-ID'] = tenantCode;
      // Only log on first request or when tenant changes
      if (!window.lastLoggedTenantId || window.lastLoggedTenantId !== tenantCode) {
        console.log('Student API: Added X-Tenant-ID header:', tenantCode);
        window.lastLoggedTenantId = tenantCode;
      }
    } else {
      // Try to extract tenant from JWT token
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.tenantId) {
            config.headers['X-Tenant-ID'] = payload.tenantId;
            // Only log on first request or when tenant changes
            if (!window.lastLoggedTenantId || window.lastLoggedTenantId !== payload.tenantId) {
              console.log('Student API: Added X-Tenant-ID from JWT:', payload.tenantId);
              window.lastLoggedTenantId = payload.tenantId;
            }
          } else {
            console.warn('Student API: No tenantCode in localStorage and no tenantId in JWT for request:', config.url);
          }
        } catch (error) {
          console.warn('Student API: Failed to extract tenant from JWT token:', error);
        }
      }
    }

    // Add user email header - extract from JWT token
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.email || payload.sub) {
          const userEmail = payload.email || payload.sub;
          config.headers['X-User-Email'] = userEmail;
          // Only log on first request or when email changes
          if (!window.lastLoggedUserEmail || window.lastLoggedUserEmail !== userEmail) {
            console.log('Student API: Added X-User-Email header:', userEmail);
            window.lastLoggedUserEmail = userEmail;
          }
        } else {
          console.warn('Student API: No email found in JWT token for request:', config.url);
        }
      } catch (error) {
        console.warn('Student API: Failed to extract email from JWT token:', error);
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  response => response,
  async error => {
    let sessionRetained = false;
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/')) {
      sessionRetained = !await confirmSessionFailure(error.config?.__sessionToken);
    }
    return Promise.reject(rememberApiError(error, sessionRetained));
  }
);

// Utility functions
export const setAuthToken = (token: string) => {
  if (token) {
    localStorage.setItem('token', token);
    apiClient.defaults.headers.Authorization = `Bearer ${token}`;
  } else {
    localStorage.removeItem('token');
    delete apiClient.defaults.headers.Authorization;
  }
};

export const getAuthToken = () => {
  return localStorage.getItem('token');
};

export const isAuthenticated = () => {
  return !!getAuthToken();
};

export default apiClient;
